import { SoraneApp } from './app.ts';
import type { SoraneConfig } from './api.ts';

function scriptBaseUrl(): string | null {
  const current = document.currentScript;
  if (current instanceof HTMLScriptElement && current.src) {
    const url = new URL(current.src);
    return `${url.origin}${url.pathname.replace(/\/[^/]*$/, '')}`;
  }

  for (const script of document.querySelectorAll('script[src]')) {
    const src = (script as HTMLScriptElement).src;
    if (src.includes('sorane-web')) {
      const url = new URL(src);
      return `${url.origin}${url.pathname.replace(/\/[^/]*$/, '')}`;
    }
  }

  return null;
}

function normalizeConfig(raw: unknown): SoraneConfig {
  if (raw === null || typeof raw !== 'object') {
    throw new Error('sorane config must be an object');
  }

  const obj = raw as Record<string, unknown>;
  const endpoint = typeof obj.endpoint === 'string' ? obj.endpoint.trim() : '';
  const siteId =
    typeof obj.siteId === 'string'
      ? obj.siteId.trim()
      : typeof obj.site_id === 'string'
        ? obj.site_id.trim()
        : '';

  if (!siteId) throw new Error('sorane config: siteId is required');

  const title = typeof obj.title === 'string' ? obj.title.trim() : undefined;
  const lead = typeof obj.lead === 'string' ? obj.lead.trim() : undefined;

  return {
    siteId,
    endpoint: endpoint || undefined,
    ...(title ? { title } : {}),
    ...(lead ? { lead } : {}),
  };
}

async function loadConfig(el: HTMLElement): Promise<SoraneConfig> {
  const inline = el.getAttribute('data-sorane-config');
  if (inline) {
    return normalizeConfig(JSON.parse(inline));
  }

  const configUrl =
    el.getAttribute('data-sorane-config-url') ??
    (() => {
      const base = scriptBaseUrl();
      return base ? `${base}/sorane-web.json` : null;
    })();

  if (!configUrl) {
    throw new Error('sorane config not found (set data-sorane-config or sorane-web.json)');
  }

  const res = await fetch(configUrl);
  if (!res.ok) throw new Error(`failed to load sorane config (${res.status})`);
  return normalizeConfig(await res.json());
}

async function mountElement(el: HTMLElement): Promise<void> {
  const config = await loadConfig(el);
  const headerEl = document.getElementById('sorane-header-actions');
  const app = new SoraneApp(el, config, headerEl);
  await app.mount();
}

function autoInit(): void {
  const targets = document.querySelectorAll<HTMLElement>('[data-sorane-auto]');
  for (const el of targets) {
    void mountElement(el).catch((err) => {
      console.error('[sorane]', err);
      el.textContent = err instanceof Error ? err.message : 'sorane failed to initialize';
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', autoInit);
} else {
  autoInit();
}

export { SoraneApp, loadConfig, mountElement };
export type { SoraneConfig } from './api.ts';